import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { useSuccessToast } from '../../store/successToastStore';
import { InputField } from '../../components/InputField';
import { updateProfile } from '../../api/auth';
interface EditProfileProps {
  isPanel?: boolean;
}
export function EditProfile({
  isPanel = false
}: EditProfileProps) {
  const navigate = useNavigate();
  const {
    user,
    token,
    updateUser
  } = useAuth();
  const {
    showToast
  } = useSuccessToast();
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardSheet, setShowDiscardSheet] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Check for changes
  useEffect(() => {
    const changed = formData.fullName !== (user?.fullName || '') || formData.email !== (user?.email || '') || formData.phone !== (user?.phone || '');
    setHasChanges(changed);
  }, [formData, user]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async () => {
    if (!validate() || !hasChanges || !token) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const updatedUser = await updateProfile(token, {
        fullName: formData.fullName,
        phone: formData.phone
      });
      updateUser(updatedUser);
      showToast('Profile updated successfully');
      if (!isPanel) {
        navigate('/app/profile');
      }
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };
  const handleBack = () => {
    if (hasChanges) {
      setShowDiscardSheet(true);
    } else {
      navigate('/app/profile');
    }
  };
  const content = <main className="w-full max-w-[600px] mx-auto pb-20 md:pb-0 animate-fadeInUp" data-id="element-1870">
      {/* Header */}
      {!isPanel && <div className="flex items-center justify-between mb-8" data-id="element-1871">
          <div className="flex items-center" data-id="element-1872">
            <button onClick={handleBack} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-2" aria-label="Go back" data-id="element-1873">
              <ArrowLeft size={24} className="text-text-primary" data-id="element-1874" />
            </button>
            <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-1875">
              Edit Profile
            </h1>
          </div>

          <button onClick={handleSave} disabled={!hasChanges || isSaving} className={`text-[15px] font-semibold transition-colors ${hasChanges ? 'text-primary' : 'text-text-secondary opacity-50'}`} data-id="element-1876">
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>}

      {isPanel && <div className="flex items-center justify-between mb-8" data-id="element-1877">
          <h2 className="text-[24px] font-semibold text-text-primary" data-id="element-1878">
            Edit Profile
          </h2>
          <button onClick={handleSave} disabled={!hasChanges || isSaving} className={`px-4 py-2 rounded-pill text-[14px] font-semibold transition-colors ${hasChanges ? 'bg-primary text-white' : 'bg-secondary text-text-secondary cursor-not-allowed'}`} data-id="element-1879">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>}

      {/* Error Banner */}
      {saveError && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center justify-between animate-slideDown" data-id="element-1880">
          <span className="text-[14px] text-danger font-medium" data-id="element-1881">
            {saveError}
          </span>
          <button onClick={() => setSaveError(null)} className="text-danger text-[13px] font-semibold ml-4" data-id="element-1882">
            Dismiss
          </button>
        </div>}

      {/* Form */}
      <div className="bg-white rounded-[24px] p-6 shadow-card" data-id="element-1883">
        <div className="space-y-6" data-id="element-1884">
          <div data-id="element-1885">
            <label htmlFor="fullName" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-1886">
              Full Name
            </label>
            <InputField id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Enter your full name" icon={<User size={20} data-id="element-1888" />} error={errors.fullName} className="mb-0" data-id="element-1887" />
          </div>

          <div data-id="element-1889">
            <label htmlFor="email" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-1890">
              Email Address
            </label>
            <InputField id="email" name="email" type="email" value={formData.email} readOnly disabled placeholder="Enter your email" icon={<Mail size={20} data-id="element-1892" />} className="mb-1 opacity-70 cursor-not-allowed" data-id="element-1891" />
            <p className="text-[12px] text-text-secondary ml-1" data-id="element-1893">
              Email cannot be changed
            </p>
          </div>

          <div data-id="element-1894">
            <label htmlFor="phone" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-1895">
              Phone Number (optional)
            </label>
            <InputField id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="Enter your phone number" icon={<Phone size={20} data-id="element-1897" />} error={errors.phone} className="mb-0" data-id="element-1896" />
          </div>
        </div>
      </div>

      {/* Discard Changes Bottom Sheet */}
      <AnimatePresence data-id="element-1898">
        {showDiscardSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowDiscardSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1899" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)]" data-id="element-1900">
              <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-6" data-id="element-1901" />
              <div className="text-center" data-id="element-1902">
                <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-1903">
                  Discard changes?
                </h2>
                <p className="text-[15px] text-text-secondary mb-8" data-id="element-1904">
                  You have unsaved changes. Are you sure you want to go back?
                </p>

                <button onClick={() => navigate('/app/profile')} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium text-[16px] mb-3" data-id="element-1905">
                  Discard
                </button>
                <button onClick={() => setShowDiscardSheet(false)} className="w-full h-[52px] bg-transparent text-text-secondary rounded-[14px] font-medium text-[16px]" data-id="element-1906">
                  Keep Editing
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
  return content;
}